/*----------------------------------
- HOME SCREENER BLUEPRINT PLAYBACK
----------------------------------*/

import type { HomeScreenerBlueprintVariant } from '@/common/catalogs/landing/homeScreenerBlueprint';

/*----------------------------------
- TYPES
----------------------------------*/

export type HomeScreenerBlueprintLoadRequestInput = {
    loadedVariant: HomeScreenerBlueprintVariant | null;
    shouldLoad: boolean;
    variant: HomeScreenerBlueprintVariant;
    variantResolved: boolean;
};

export type HomeScreenerBlueprintPlaybackInput = {
    documentVisible: boolean;
    explicitlyRequested: boolean;
    hasEnteredLoadMargin: boolean;
    /** The visitor has scrolled, pointed, typed or touched since the page loaded. */
    hasInteracted: boolean;
    inViewport: boolean;
    /** Set where the figure sits right under a hero: no automatic load before an interaction. */
    interactionRequired: boolean;
    pausedByUser: boolean;
    playbackBlocked: boolean;
    prefersReducedMotion: boolean;
    savesData: boolean;
};

export type HomeScreenerBlueprintPlaybackState = {
    automaticallyAllowed: boolean;
    shouldLoad: boolean;
    shouldPlay: boolean;
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

export const getHomeScreenerBlueprintPlaybackState = ({
    documentVisible,
    explicitlyRequested,
    hasEnteredLoadMargin,
    hasInteracted,
    inViewport,
    interactionRequired,
    pausedByUser,
    playbackBlocked,
    prefersReducedMotion,
    savesData,
}: HomeScreenerBlueprintPlaybackInput): HomeScreenerBlueprintPlaybackState => {
    /*
     * Right under a hero the load margin is reached on arrival, so the film's bytes competed
     * with the page's first paint: on Home it was the phone LCP element (5 to 6 s). Waiting for
     * a first interaction keeps the poster alone on the critical path; the play button is an
     * explicit request and still loads at once.
     */
    const automaticallyAllowed = !prefersReducedMotion && !savesData && (!interactionRequired || hasInteracted);
    const playbackAllowed = automaticallyAllowed || explicitlyRequested;
    const shouldLoad = playbackAllowed && (hasEnteredLoadMargin || explicitlyRequested);
    const shouldPlay =
        shouldLoad && documentVisible && inViewport && !pausedByUser && !playbackBlocked;

    return {
        automaticallyAllowed,
        shouldLoad,
        shouldPlay,
    };
};

/**
 * Whether the media element should be asked to load its sources right now.
 *
 * Two rules the element cannot enforce itself. Swapping `<source>` children
 * never re-runs its resource selection, so crossing the breakpoint has to
 * re-request the load, and exactly once per cut. And nothing may be requested
 * before the viewport has answered which cut applies: the figure sits directly
 * under the hero, so its load margin is reached on arrival, and a request made
 * a render too early would spend the landscape pair's bytes on a phone and then
 * the portrait pair's on top of them.
 */
export const shouldRequestHomeScreenerBlueprintLoad = ({
    loadedVariant,
    shouldLoad,
    variant,
    variantResolved,
}: HomeScreenerBlueprintLoadRequestInput): boolean =>
    shouldLoad && variantResolved && loadedVariant !== variant;
